import React, { FC, useEffect, useState } from 'react';

import { Modal } from '../../../components/modal/index';
import { TEAM_ROLE_SUGGESTIONS, TeamMember } from '../../../types/team.types';
import { getFieldErrors, memberFormSchema, MemberFormValues } from '../team.validation';
import { DateField, TextField } from '../team-field';

export type MemberFormModalProps = {
  isOpen: boolean;
  isPending: boolean;
  member: null | TeamMember;
  onDismiss: () => void;
  onSubmit: (values: MemberFormValues) => void;
};

const emptyValues: MemberFormValues = { name: '', role: '', email: '', startDate: '' };

export const MemberFormModal: FC<MemberFormModalProps> = ({
  isOpen,
  isPending,
  member,
  onDismiss,
  onSubmit,
}) => {
  const [values, setValues] = useState<MemberFormValues>(emptyValues);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!isOpen) return;

    setErrors({});
    setValues(
      member
        ? {
            name: member.name,
            role: member.role,
            email: member.email,
            startDate: member.startDate,
          }
        : emptyValues,
    );
  }, [isOpen, member]);

  const setField = (field: keyof MemberFormValues) => (value: string) =>
    setValues((prev) => ({ ...prev, [field]: value }));

  const handleSave = () => {
    const result = memberFormSchema.safeParse(values);

    if (!result.success) {
      setErrors(getFieldErrors(result.error));
      return;
    }

    onSubmit(result.data);
  };

  return (
    <Modal
      className="max-w-[480px]"
      crossButton
      dismissButtonContent="Cancel"
      isOpen={isOpen}
      onDismiss={onDismiss}
      onHardClose={onDismiss}
      onSuccess={isPending ? undefined : handleSave}
      successButtonClassName="bg-brand-700"
      successButtonContent={isPending ? 'Saving…' : member ? 'Save changes' : 'Add person'}
      title={member ? 'Edit team member' : 'Add team member'}
    >
      <div className="flex flex-col gap-4">
        <TextField
          error={errors.name}
          label="Name"
          onChange={setField('name')}
          placeholder="Full name"
          value={values.name}
        />
        <TextField
          error={errors.role}
          label="Role"
          list="team-role-suggestions"
          onChange={setField('role')}
          placeholder="e.g. Developer"
          value={values.role}
        />
        <datalist id="team-role-suggestions">
          {TEAM_ROLE_SUGGESTIONS.map((role) => (
            <option key={role} value={role} />
          ))}
        </datalist>
        <TextField
          error={errors.email}
          label="Email"
          onChange={setField('email')}
          placeholder="name@capquest.io"
          type="email"
          value={values.email}
        />
        <DateField
          error={errors.startDate}
          label="Start date (used to prorate annual PTO)"
          onChange={setField('startDate')}
          value={values.startDate}
        />
      </div>
    </Modal>
  );
};
